import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import TurnstileWidget from './TurnstileWidget'
import { useCommonT } from '../../i18n'
import { OVERLAY_ABOVE_AD, PANEL_FITS_OVERLAY } from '../../lib/adInset'

// The sign-in modal. One component for every method: Google, Apple,
// Discord, magic link, email + password, and password reset.
//
// Two rules run through all of it:
//  1. Every post-submit state is a MODAL STATE, not a toast. Someone who just
//     asked for an email is told so in the same box they asked from, instead
//     of being dropped back on a dead button.
//  2. The email copy is ENUMERATION-SAFE. It never confirms or denies that an
//     address has an account, because "that email is already registered" is a
//     free account-existence oracle for anyone who wants to farm it.

type View = 'methods' | 'password-signin' | 'password-signup' | 'reset'
          | 'link-sent' | 'confirm-sent' | 'reset-sent'

// The mismatch and too-short messages are common:auth.password_mismatch and
// .password_too_short, shared with SetPasswordModal and the account page.
const MIN_PASSWORD_LENGTH = 8

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 48 48" className="w-4 h-4 shrink-0" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  )
}

function AppleGlyph() {
  return (
    <svg viewBox="0 0 384 512" className="w-4 h-4 shrink-0 fill-current" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  )
}

function DiscordGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0 fill-current" aria-hidden="true">
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
  )
}

// Views where closing means the visitor gave up: nothing was sent that could
// still sign them in later (the "check your email" views are excluded).
const ABANDONABLE: ReadonlySet<View> = new Set<View>(['methods', 'password-signin', 'password-signup', 'reset'])

export default function AuthModal({ open, onClose, onAbandon, returnNonce }: {
  open: boolean
  onClose: () => void
  /** Optional. Threaded into every sign-in call that builds a return URL, so
   *  that URL carries `?cb_return=<nonce>` (the Watchtower teaser's one-shot
   *  return marker, see authReturn.ts). Omitted, every call is made with
   *  exactly the arguments it always had. */
  returnNonce?: string | null
  /** Optional. Called (before onClose) when the backdrop dismisses the modal
   *  from a form view, i.e. the visitor left without signing in and without
   *  a pending email link. Never called on a successful sign-in. */
  onAbandon?: () => void
}) {
  const {
    signInWithGoogleV2, signInWithApple, signInWithDiscord,
    signInWithEmail, signInWithPassword, signUpWithPassword, resetPassword,
  } = useAuth()
  const t = useCommonT()
  // eslint-disable-next-line i18next/no-literal-string -- state code
  const [view, setView] = useState<View>('methods')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [captchaToken, setCaptchaToken] = useState<string | undefined>(undefined)
  const [captchaNonce, setCaptchaNonce] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Shared-device hygiene: a reopen starts clean, so the previous visitor's
  // address is never sitting in the field for the next one.
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line i18next/no-literal-string -- state code
      setView('methods'); setEmail(''); setPassword(''); setConfirm(''); setError(null)
    }
  }, [open])

  if (!open) return null

  // Navigation is INERT while a request is in flight. Every navigation
  // control is disabled={busy} as well; this is the second lock, because a
  // view change mid-request lands the result somewhere the user is no longer
  // looking (an error under a form they left, or a success that yanks them
  // out of the view they just opened).
  const go = (next: View) => {
    if (busy) return
    setView(next); setError(null)
  }

  const run = async (fn: () => Promise<{ error: string | null }>, onOk: () => void) => {
    setBusy(true); setError(null)
    try {
      const { error: err } = await fn()
      if (err) setError(err); else onOk()
    } finally {
      setBusy(false)
      // Turnstile tokens are single-use: force a fresh one for any retry.
      setCaptchaToken(undefined)
      setCaptchaNonce(n => n + 1)
    }
  }

  const submitSignup = () => {
    if (password !== confirm) { setError(t('auth.password_mismatch')); return }
    if (password.length < MIN_PASSWORD_LENGTH) { setError(t('auth.password_too_short', { n: MIN_PASSWORD_LENGTH })); return }
    void run(() => (returnNonce
      ? signUpWithPassword(email, password, captchaToken, returnNonce)
      // eslint-disable-next-line i18next/no-literal-string -- state code
      : signUpWithPassword(email, password, captchaToken)), () => setView('confirm-sent'))
  }

  const providerButton = (label: string, glyph: React.ReactNode, action: () => Promise<{ error: string | null }>) => (
    <button
      type="button"
      onClick={() => void run(action, () => {})}
      disabled={busy}
      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-sm text-gray-100 hover:bg-gray-700 hover:border-gray-500 disabled:opacity-50 transition-colors"
    >
      {glyph}
      {label}
    </button>
  )

  const emailField = (
    <div className="space-y-1">
      <label htmlFor="auth-email" className="block text-xs text-gray-400">{t('auth.email')}</label>
      <input
        id="auth-email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        disabled={busy}
        className="w-full px-3 py-2 rounded-lg bg-gray-950 border border-gray-700 text-sm text-gray-100 placeholder-gray-600 focus:border-gray-500 focus:outline-none disabled:opacity-50"
      />
    </div>
  )

  const passwordField = (autoComplete: 'current-password' | 'new-password') => (
    <div className="space-y-1">
      <label htmlFor="auth-password" className="block text-xs text-gray-400">{t('auth.password')}</label>
      <input
        id="auth-password"
        type="password"
        autoComplete={autoComplete}
        value={password}
        onChange={e => setPassword(e.target.value)}
        disabled={busy}
        className="w-full px-3 py-2 rounded-lg bg-gray-950 border border-gray-700 text-sm text-gray-100 focus:border-gray-500 focus:outline-none disabled:opacity-50"
      />
    </div>
  )

  const confirmField = (
    <div className="space-y-1">
      <label htmlFor="auth-confirm" className="block text-xs text-gray-400">{t('auth.confirm_password')}</label>
      <input
        id="auth-confirm"
        type="password"
        autoComplete="new-password"
        value={confirm}
        onChange={e => setConfirm(e.target.value)}
        disabled={busy}
        className="w-full px-3 py-2 rounded-lg bg-gray-950 border border-gray-700 text-sm text-gray-100 focus:border-gray-500 focus:outline-none disabled:opacity-50"
      />
    </div>
  )

  const captcha = <TurnstileWidget onToken={setCaptchaToken} resetNonce={captchaNonce} />

  const errorLine = error && (
    <p className="text-xs text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded p-2">
      {error}
    </p>
  )

  // eslint-disable-next-line i18next/no-literal-string -- CSS classes
  const primaryButtonClass = 'w-full px-4 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors'
  // eslint-disable-next-line i18next/no-literal-string -- CSS classes
  const quietButtonClass = 'text-xs text-gray-400 hover:text-gray-200 transition-colors'

  const doneButton = (
    <button type="button" onClick={onClose} className={primaryButtonClass}>{t('auth.done')}</button>
  )

  let body: React.ReactNode = null

  if (view === 'methods') {
    body = (
      <>
        <h2 className="text-lg font-semibold text-gray-100">{t('auth.title')}</h2>
        <div className="space-y-2">
          {providerButton(t('auth.continue_google'), <GoogleGlyph />, () => (returnNonce ? signInWithGoogleV2(returnNonce) : signInWithGoogleV2()))}
          {providerButton(t('auth.continue_apple'), <AppleGlyph />, () => (returnNonce ? signInWithApple(returnNonce) : signInWithApple()))}
          {providerButton(t('auth.continue_discord'), <DiscordGlyph />, () => (returnNonce ? signInWithDiscord(returnNonce) : signInWithDiscord()))}
        </div>
        <div className="flex items-center gap-3">
          <span className="flex-1 h-px bg-gray-800" />
          <span className="text-[11px] uppercase tracking-wide text-gray-600">{t('auth.or')}</span>
          <span className="flex-1 h-px bg-gray-800" />
        </div>
        {emailField}
        {captcha}
        {errorLine}
        <button
          type="button"
          onClick={() => void run(
            () => (returnNonce
              ? signInWithEmail(email, captchaToken, returnNonce)
              : signInWithEmail(email, captchaToken)),
            () => setView('link-sent'),
          )}
          disabled={busy}
          className={primaryButtonClass}
        >
          {t('auth.send_link')}
        </button>
        <button type="button" onClick={() => go('password-signin')} disabled={busy} className={quietButtonClass}>
          {t('auth.use_password')}
        </button>
      </>
    )
  } else if (view === 'password-signin') {
    body = (
      <>
        <h2 className="text-lg font-semibold text-gray-100">{t('auth.password_title')}</h2>
        {emailField}
        {/* eslint-disable-next-line i18next/no-literal-string -- autocomplete token */}
        {passwordField('current-password')}
        {captcha}
        {errorLine}
        <button
          type="button"
          onClick={() => void run(
            () => signInWithPassword(email, password, captchaToken),
            onClose,
          )}
          disabled={busy}
          className={primaryButtonClass}
        >
          {t('auth.sign_in')}
        </button>
        <div className="flex items-center justify-between gap-3">
          <button type="button" onClick={() => go('reset')} disabled={busy} className={quietButtonClass}>
            {t('auth.forgot')}
          </button>
          <button type="button" onClick={() => go('password-signup')} disabled={busy} className={quietButtonClass}>
            {t('auth.new_here')}
          </button>
        </div>
        <button type="button" onClick={() => go('methods')} disabled={busy} className={quietButtonClass}>{t('auth.back')}</button>
      </>
    )
  } else if (view === 'password-signup') {
    body = (
      <>
        <h2 className="text-lg font-semibold text-gray-100">{t('auth.create_title')}</h2>
        {emailField}
        {/* eslint-disable-next-line i18next/no-literal-string -- autocomplete token */}
        {passwordField('new-password')}
        {confirmField}
        {captcha}
        {errorLine}
        <button
          type="button"
          onClick={submitSignup}
          disabled={busy}
          className={primaryButtonClass}
        >
          {t('auth.create')}
        </button>
        <button type="button" onClick={() => go('password-signin')} disabled={busy} className={quietButtonClass}>{t('auth.back')}</button>
      </>
    )
  } else if (view === 'reset') {
    body = (
      <>
        <h2 className="text-lg font-semibold text-gray-100">{t('auth.reset_title')}</h2>
        <p className="text-xs text-gray-400">{t('auth.reset_body')}</p>
        {emailField}
        {captcha}
        {errorLine}
        <button
          type="button"
          onClick={() => void run(
            () => resetPassword(email, captchaToken),
            () => setView('reset-sent'),
          )}
          disabled={busy}
          className={primaryButtonClass}
        >
          {t('auth.send_reset')}
        </button>
        <button type="button" onClick={() => go('password-signin')} disabled={busy} className={quietButtonClass}>{t('auth.back')}</button>
      </>
    )
  } else if (view === 'link-sent') {
    body = (
      <>
        <h2 className="text-lg font-semibold text-gray-100">{t('auth.check_email')}</h2>
        <p className="text-sm text-gray-300">{t('auth.link_sent', { email })}</p>
        {doneButton}
      </>
    )
  } else if (view === 'confirm-sent') {
    body = (
      <>
        <h2 className="text-lg font-semibold text-gray-100">{t('auth.check_email')}</h2>
        <p className="text-sm text-gray-300">{t('auth.confirm_sent', { email })}</p>
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => go('password-signin')} disabled={busy} className={quietButtonClass}>
            {t('auth.sign_in_instead')}
          </button>
          <button type="button" onClick={() => go('reset')} disabled={busy} className={quietButtonClass}>
            {t('auth.reset_password')}
          </button>
        </div>
        {doneButton}
      </>
    )
  } else {
    body = (
      <>
        <h2 className="text-lg font-semibold text-gray-100">{t('auth.check_email')}</h2>
        <p className="text-sm text-gray-300">{t('auth.reset_sent', { email })}</p>
        {doneButton}
      </>
    )
  }

  return (
    <div
      className={`fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 ${OVERLAY_ABOVE_AD}`}
      onClick={() => {
        if (busy) return
        if (ABANDONABLE.has(view)) onAbandon?.()
        onClose()
      }}
    >
      <div
        className={`bg-gray-900 border border-gray-700 rounded-lg p-6 max-w-sm w-full space-y-4 ${PANEL_FITS_OVERLAY}`}
        onClick={e => e.stopPropagation()}
      >
        {body}
      </div>
    </div>
  )
}
